import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Megaphone, Sparkles, Bot, Server, ArrowUpRight } from 'lucide-react';

const SERVICES = [
  {
    icon: Code2,
    title: 'Web & App Development',
    desc: 'Production-grade web platforms, mobile apps, and SaaS products. Built fast, built right.',
    span: 'md:col-span-2 md:row-span-2',
    featured: true,
  },
  {
    icon: Megaphone,
    title: 'Digital Marketing',
    desc: 'SEO, paid acquisition, content engines, and brand systems that convert.',
    span: '',
  },
  {
    icon: Sparkles,
    title: 'AI Solutions',
    desc: 'Custom LLM workflows, chatbots, and intelligence layered into your business.',
    span: '',
  },
  {
    icon: Bot,
    title: 'Automation',
    desc: 'Stop doing what software should do. Workflows that scale your team.',
    span: '',
  },
  {
    icon: Server,
    title: 'IT Consulting',
    desc: 'Architecture audits, cloud strategy, and migrations — handled.',
    span: '',
  },
];

const Card = ({ service, index }) => {
  const Icon = service.icon;

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMove}
      data-testid={`service-card-${index}`}
      className={`group relative bg-brand-surface border border-white/5 hover:border-white/20 p-8 lg:p-10 flex flex-col gap-5 overflow-hidden transition-all duration-300 ${service.span} ${
        service.featured ? 'min-h-[420px] md:min-h-[520px]' : 'min-h-[260px]'
      }`}
    >
      <div className="tracing-beam" />

      <div className="relative z-10 flex items-start justify-between">
        <div className={`w-12 h-12 flex items-center justify-center border ${service.featured ? 'border-brand-primary/40 bg-brand-primary/10' : 'border-white/10 bg-white/5'}`}>
          <Icon size={20} className={service.featured ? 'text-brand-primary' : 'text-white'} />
        </div>
        <span className="font-manrope text-xs text-white/30">
          0{index + 1}
        </span>
      </div>

      <div className="relative z-10 mt-auto">
        <h3 className={`font-outfit font-bold tracking-tight text-white ${service.featured ? 'text-4xl lg:text-5xl' : 'text-2xl lg:text-3xl'} mb-3`}>
          {service.title}
        </h3>
        <p className="font-manrope text-sm lg:text-base text-white/60 leading-relaxed max-w-md">
          {service.desc}
        </p>
        <div className="mt-6 flex items-center gap-2 text-white/40 group-hover:text-brand-primary transition-colors">
          <span className="font-manrope text-xs uppercase tracking-[0.2em]">Learn more</span>
          <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-40 px-6 lg:px-12">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 mb-16 lg:mb-24">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1.5 h-1.5 bg-brand-primary" />
              <span className="font-manrope text-xs uppercase tracking-[0.3em] text-white/60">
                01 — Services
              </span>
            </div>
            <h2 className="font-outfit font-black tracking-tighter text-white text-5xl lg:text-7xl leading-[0.9]">
              What we<br />actually<span className="italic font-light text-brand-primary"> do.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="font-manrope text-base lg:text-lg text-white/60 leading-relaxed">
              Five disciplines. One operating system. Each capability is engineered to compound the others —
              so your digital presence doesn&apos;t just look modern, it actually performs.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
          {SERVICES.map((s, i) => (
            <Card key={s.title} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
